import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Clock, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Logo } from "@/components/brand";
import { Showcase } from "@/components/showcase";
import { Button, IconButton } from "@/components/ui/button";
import { Field, Form, InputGroup } from "@/components/ui/field";
import { Tooltip } from "@/components/ui/tooltip";
import { api, errorMessage } from "@/services/api";
import { cn, formatWait } from "@/utils/utils";
import type { LoginResponse, ProblemDetail } from "@/meta/types";
import { useTranslation } from "@/store/locale";
import {
  PASSWORD_CHANGE_DEADLINE_KEY,
  PASSWORD_CHANGE_TOKEN_KEY,
  PASSWORD_CHANGE_USER_KEY,
} from "@/pages/SetPasswordPage";
import { useAuthStore } from "@/store/auth";

/**
 * The field is labelled "Employee ID" because that is what the person was given,
 * and sent as `user_name` because that is what the API calls it. Naming a field
 * after the column behind it helps nobody standing at a till.
 */
const schema = z.object({
  user_name: z.string().trim().min(1),
  password: z.string().min(1),
});

type LoginInput = z.infer<typeof schema>;

/**
 * What is actually posted. `takeover` is not a form field — nobody types it,
 * and it must never ride along on an ordinary Continue — so it sits outside
 * the schema and is added only by the button that asks for it.
 */
type LoginSubmission = LoginInput & { takeover?: boolean };

/**
 * Refusals sign-in cannot fix, which the reset request can.
 *
 * The problem type's fragment is the same string the reset reasons enum uses,
 * so it travels as both the routing decision and the preset.
 */
const FORWARDED = new Set(["password_expired", "account_locked"]);

/**
 * What the button waits for before it will submit.
 *
 * Both come from the API's own schemas, so neither can lock out an account
 * that would otherwise have signed in: `UserName` is `min_length=3`, so no
 * shorter account exists, and `Password` is `min_length=8` on every path that
 * can set one — the reset, the admin reset, and `createsuperuser` alike.
 */
const MIN_EMPLOYEE_ID = 3;
const MIN_PASSWORD = 8;

function LoginPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const signIn = useAuthStore((state) => state.signIn);
  const [revealed, setRevealed] = useState(false);
  // When the API says "too many attempts", this is the moment it lifts.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Set when someone presses a button that is not ready, so the reason stays
  // on screen instead of only appearing under the pointer.
  const [showHint, setShowHint] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(schema),
    defaultValues: { user_name: "", password: "" },
  });
  const [employeeId, password] = useWatch({
    control: form.control,
    name: ["user_name", "password"],
  });

  const mutation = useMutation({
    mutationFn: async (input: LoginSubmission) =>
      (await api.post<LoginResponse>("/auth/login", input)).data,
    onSuccess: (response) => {
      // A forced change is a wall: sign-in gave a five-minute token instead of
      // a session, and the set-password screen spends it.
      if (response.must_change_password || !response.access_token || !response.refresh_token) {
        if (response.password_change_token) {
          sessionStorage.setItem(PASSWORD_CHANGE_TOKEN_KEY, response.password_change_token);
          // The API says how long it lives; the next screen counts it down.
          sessionStorage.setItem(
            PASSWORD_CHANGE_DEADLINE_KEY,
            String(Date.now() + response.expires_in * 1000),
          );
          sessionStorage.setItem(PASSWORD_CHANGE_USER_KEY, form.getValues("user_name"));
        }
        toast.info(t("login.mustChangePassword"));
        void navigate("/set-password");
        return;
      }
      signIn({
        ...response,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      });
      // The shell raises the expiry warning — it has the profile and the
      // policy, and it is there on every load rather than only at the moment
      // of signing in.
      void navigate("/");
    },
    // An expired password or a locked account goes straight to the reset
    // request with the id filled in and the reason fixed, as the legacy
    // sign-in does: neither is anything the person can fix on this screen.
    onError: (error) => {
      const response = (error as { response?: { status?: number; data?: ProblemDetail } })
        .response;
      // The API sends the seconds left in the problem body, so the countdown
      // never has to be read back out of a translated sentence.
      if (response?.status === 429) {
        const seconds = response.data?.retry_after ?? 60;
        setNow(Date.now());
        setLockedUntil(Date.now() + seconds * 1000);
        return;
      }
      const code = (response?.data?.type ?? "").split("#")[1] ?? "";
      if (FORWARDED.has(code))
        void navigate(
          `/reset-password-request?reason=${encodeURIComponent(code)}` +
            `&employee_id=${encodeURIComponent(form.getValues("user_name"))}`,
        );
    },
  });

  // A 409 on sign-in means the account holds a live session somewhere else.
  // Read off the problem document rather than kept in state: it is true of the
  // last attempt and of nothing else, so it clears itself when one is retried.
  const alreadySignedIn =
    (
      (mutation.error as { response?: { data?: ProblemDetail } } | null)?.response?.data
        ?.type ?? ""
    ).split("#")[1] === "active_session_exists";

  // One timer while a lock is running; it clears itself when the wait is over
  // so the form comes back without a reload.
  useEffect(() => {
    if (lockedUntil === null) return;
    const id = setInterval(() => {
      if (Date.now() >= lockedUntil) {
        setLockedUntil(null);
        mutation.reset();
      } else {
        setNow(Date.now());
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil, mutation]);

  const waiting = lockedUntil === null ? 0 : Math.max(0, Math.ceil((lockedUntil - now) / 1000));
  const locked = waiting > 0;

  // Why the button is not ready yet, in the person's own terms. Null means it is.
  const idShort = (employeeId ?? "").trim().length < MIN_EMPLOYEE_ID;
  const passwordShort = (password ?? "").length < MIN_PASSWORD;
  const hint =
    idShort && passwordShort
      ? t("login.hintBoth")
      : idShort
        ? t("login.hintId").replace("{n}", String(MIN_EMPLOYEE_ID))
        : passwordShort
          ? t("login.hintPassword").replace("{n}", String(MIN_PASSWORD))
          : null;
  const blocked = hint !== null || locked;

  const errors = form.formState.errors;

  return (
    <div className="grid flex-1 gap-8 px-5 pt-6 pb-24 sm:px-8 lg:grid-cols-[minmax(0,1fr)_min(571px,42%)] lg:items-stretch lg:gap-10 lg:px-8 lg:py-8">
      <main className="flex flex-col">
        <div className="flex flex-1 items-center py-10 lg:py-6">
          <div className="mx-auto w-full max-w-[32.5rem]">
            <Logo className="mb-10 w-[140px]" />
            <h1 className="text-title-1 text-heading">{t("login.welcome")}</h1>
            <p className="text-fg-muted text-body mt-2">{t("login.subtitle")}</p>
            <Form
              className="mt-8 grid gap-4"
              onSubmit={(event) => {
                // The button is `aria-disabled`, not `disabled`, so a press
                // still lands here — and answers the question it was pressed to
                // ask instead of doing nothing at all.
                if (blocked) {
                  event.preventDefault();
                  setShowHint(true);
                  if (idShort) form.setFocus("user_name");
                  else if (passwordShort) form.setFocus("password");
                  return;
                }
                void form.handleSubmit((values) => mutation.mutate(values))(event);
              }}
              noValidate
            >
              {/* Waiting is not a failure, so it does not look like one: amber,
                  a clock, and a sentence that says the account is fine. */}
              {locked ? (
                <div
                  role="status"
                  className="border-warning/25 bg-warning-soft text-callout flex items-start gap-2.5 rounded-xl border px-3.5 py-3"
                >
                  <Clock className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="text-fg block font-medium">{t("login.lockedTitle")}</span>
                    <span className="text-fg-muted mt-0.5 block text-pretty">
                      {t("login.lockedBody").replace("{time}", formatWait(waiting))}
                    </span>
                  </span>
                </div>
              ) : null}

              {/* A refusal says what happened and, where there is one, names
                  the way out — a red line of text with no remedy is a dead end. */}
              {/* The account being signed in elsewhere is not a failed sign-in:
                  the credentials were right and there is nothing to retype. It
                  gets its own heading and its own remedy, and deliberately does
                  not read like a wrong password. */}
              {!locked && alreadySignedIn ? (
                <div
                  role="alert"
                  className="border-warning/25 bg-warning-soft text-callout flex items-start gap-2.5 rounded-xl border px-3.5 py-3"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span className="grid gap-2.5">
                    <span>
                      <strong className="block font-semibold">{t("login.alreadyOn")}</strong>
                      {t("login.alreadyOnBody")}
                    </span>
                    {/* The same sign-in, with the question answered. Its own
                        button rather than a second press of Continue: ending
                        somebody's session is not what Continue appeared to
                        offer, and a person who meant to wait must be able to
                        just not press this. `type="button"` so it submits
                        nothing — the credentials are read straight off the
                        form, which still holds what was just typed. */}
                    <Button
                      type="button"
                      variant="secondary"
                      className="justify-self-start"
                      loading={mutation.isPending}
                      onClick={() => mutation.mutate({ ...form.getValues(), takeover: true })}
                    >
                      {t("login.alreadyOnAction")}
                    </Button>
                  </span>
                </div>
              ) : !locked && mutation.isError ? (
                <p
                  role="alert"
                  className="bg-danger-soft text-danger text-callout flex items-start gap-2.5 rounded-xl px-3.5 py-3"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>{errorMessage(mutation.error, t("login.errorGeneric"))}</span>
                </p>
              ) : null}

              <Field
                label={t("login.employeeId")}
                htmlFor="user_name"
                error={errors.user_name && t("login.errorEmployeeIdRequired")}
              >
                <InputGroup
                  id="user_name"
                  icon={Mail}
                  fieldSize="lg"
                  placeholder={t("login.employeeIdPlaceholder")}
                  autoComplete="username"
                  inputMode="numeric"
                  spellCheck={false}
                  aria-invalid={Boolean(errors.user_name)}
                  {...form.register("user_name")}
                />
              </Field>

              <div className="grid gap-4">
                <Field
                  label={t("login.password")}
                  htmlFor="password"
                  error={errors.password && t("login.errorPasswordRequired")}
                >
                  <InputGroup
                    id="password"
                    icon={Lock}
                    fieldSize="lg"
                    type={revealed ? "text" : "password"}
                    placeholder={t("login.passwordPlaceholder")}
                    autoComplete="current-password"
                    aria-invalid={Boolean(errors.password)}
                    trailing={
                      <IconButton
                        label={revealed ? t("login.hidePassword") : t("login.showPassword")}
                        size="icon-sm"
                        onClick={() => setRevealed((shown) => !shown)}
                        aria-pressed={revealed}
                      >
                        {revealed ? <Eye aria-hidden /> : <EyeOff aria-hidden />}
                      </IconButton>
                    }
                    {...form.register("password")}
                  />
                </Field>

                {/* Unreachable while the request is in flight: leaving now abandons
                    it half-sent, and an anchor has no `disabled` to say so. */}
                <Link
                  to="/reset-password-request"
                  aria-disabled={mutation.isPending || undefined}
                  tabIndex={mutation.isPending ? -1 : undefined}
                  className={cn(
                    "text-accent text-subhead justify-self-end font-medium underline-offset-4 hover:underline",
                    mutation.isPending && "pointer-events-none opacity-50",
                  )}
                >
                  {t("login.resetPassword")}
                </Link>
              </div>

              <div className="mt-3 grid gap-2">
                <Tooltip label={locked ? null : hint} className="w-full" side="top">
                  <Button
                    type="submit"
                    size="xl"
                    className="w-full"
                    aria-disabled={blocked || undefined}
                    {...(blocked ? { "aria-describedby": "continue-hint" } : {})}
                    loading={mutation.isPending}
                    loadingLabel={t("login.signingIn")}
                  >
                    {locked
                      ? t("login.lockedAction").replace("{time}", formatWait(waiting))
                      : t("login.continue")}
                  </Button>
                </Tooltip>
                {/* Pressing a button that is not ready should teach, not
                    stonewall — so the reason stays put once it is asked for. */}
                {showHint && hint && !locked ? (
                  <p id="continue-hint" role="status" className="text-fg-muted text-subhead">
                    {hint}
                  </p>
                ) : null}
              </div>
            </Form>
          </div>
        </div>
      </main>

      {/* Decoration earns its place beside the form, not stacked under it on a
          phone, where it only pushes the footer out of reach. */}
      <div className="hidden lg:block">
        <Showcase />
      </div>
    </div>
  );
}

export default LoginPage;
